import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, Award, CheckCircle, Clock, BookOpen, ClipboardList, User, MessageSquare, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TranscriptPage() {
  const { user, token } = useAuth();
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchTranscript();
    }
  }, [token]);

  const fetchTranscript = async () => {
    try {
      const res = await axios.get(`${API}/my-transcript`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTranscript(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Loading transcript...</p>
      </div>
    );
  }

  const mcqAverage = transcript?.mcq_results?.length > 0
    ? (transcript.mcq_results.reduce((acc, r) => acc + (r.score || 0), 0) / transcript.mcq_results.length).toFixed(1)
    : null;

  const caseLogAvg = transcript?.case_logs?.average_score;

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="transcript-page">
      <section className="bg-[#1B2A4A] py-10 print:hidden">
        <div className="imcac-container">
          <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Academic Record</p>
          <h1 className="text-3xl font-bold text-white tracking-tight font-heading">Your Transcript</h1>
          <p className="text-slate-300 text-sm mt-1">Complete record of your examination performance</p>
        </div>
      </section>

      <div className="imcac-container py-8">
        <div className="flex justify-end mb-4 print:hidden">
          <Button onClick={handlePrint} className="bg-[#1B2A4A] text-white rounded-sm">
            <Download className="h-4 w-4 mr-2" /> Print / Download PDF
          </Button>
        </div>

        {/* Printable Transcript */}
        <div ref={printRef} className="print:p-8">
          {/* Header for Print */}
          <div className="hidden print:block mb-8 border-b-2 border-[#1B2A4A] pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-[#1B2A4A]">IMCAC</h1>
                <p className="text-sm text-slate-600">International Medical Competency Accreditation Council</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-[#1B2A4A]">OFFICIAL TRANSCRIPT</p>
                <p className="text-xs text-slate-500">Generated: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Student Info */}
          <Card className="imcac-card mb-6 print:shadow-none print:border print:border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-[#1B2A4A] text-white print:bg-slate-100 print:text-[#1B2A4A]">
                  <User className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading">{transcript?.student?.full_name || user?.full_name}</h2>
                  <p className="text-sm text-slate-500">{transcript?.student?.email || user?.email}</p>
                  {transcript?.application && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge variant="outline" className="text-xs rounded-sm">{transcript.application.specialty || transcript.application.examination}</Badge>
                      <Badge variant="outline" className="text-xs rounded-sm">Route {transcript.application.route}</Badge>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 print:grid-cols-4">
            <Card className="imcac-card print:shadow-none print:border print:border-slate-200">
              <CardContent className="p-4 text-center">
                <BookOpen className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1B2A4A]">{mcqAverage || '-'}</p>
                <p className="text-xs text-slate-500">MCQ Average</p>
              </CardContent>
            </Card>
            <Card className="imcac-card print:shadow-none print:border print:border-slate-200">
              <CardContent className="p-4 text-center">
                <Award className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1B2A4A]">{transcript?.osce?.grade || '-'}</p>
                <p className="text-xs text-slate-500">OSCE Grade</p>
              </CardContent>
            </Card>
            <Card className="imcac-card print:shadow-none print:border print:border-slate-200">
              <CardContent className="p-4 text-center">
                <ClipboardList className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1B2A4A]">{caseLogAvg?.toFixed(1) || '-'}</p>
                <p className="text-xs text-slate-500">Case Log Avg</p>
              </CardContent>
            </Card>
            <Card className="imcac-card print:shadow-none print:border print:border-slate-200">
              <CardContent className="p-4 text-center">
                <FileText className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1B2A4A]">{transcript?.case_logs?.total_submitted || 0}</p>
                <p className="text-xs text-slate-500">Total Cases</p>
              </CardContent>
            </Card>
          </div>

          {/* MCQ Examination Results */}
          <Card className="imcac-card mb-6 print:shadow-none print:border print:border-slate-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4 flex items-center">
                <BookOpen className="h-5 w-5 mr-2 text-[#C5A572]" /> MCQ Examination Results
              </h3>
              {transcript?.mcq_results?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold text-xs">Date</th>
                        <th className="text-left py-2 px-3 font-semibold text-xs">Examination</th>
                        <th className="text-center py-2 px-3 font-semibold text-xs">Score</th>
                        <th className="text-center py-2 px-3 font-semibold text-xs">Total</th>
                        <th className="text-center py-2 px-3 font-semibold text-xs">Percentage</th>
                        <th className="text-center py-2 px-3 font-semibold text-xs">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transcript.mcq_results.map((result, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-2 px-3">{new Date(result.completed_at).toLocaleDateString()}</td>
                          <td className="py-2 px-3">{result.examination || 'MCQ Exam'}</td>
                          <td className="py-2 px-3 text-center font-medium">{result.correct_count || '-'}</td>
                          <td className="py-2 px-3 text-center">{result.total_questions}</td>
                          <td className="py-2 px-3 text-center">{result.score?.toFixed(1) || '-'}%</td>
                          <td className="py-2 px-3 text-center">
                            <Badge className={`text-xs rounded-sm ${result.score >= 60 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {result.score >= 60 ? 'Pass' : 'Fail'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No MCQ examination results yet.</p>
              )}
            </CardContent>
          </Card>

          {/* OSCE Results */}
          <Card className="imcac-card mb-6 print:shadow-none print:border print:border-slate-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4 flex items-center">
                <Award className="h-5 w-5 mr-2 text-[#C5A572]" /> OSCE Examination Results
              </h3>
              {transcript?.osce?.schedule ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">Exam Date</p>
                    <p className="text-sm font-medium text-[#1B2A4A]">{new Date(transcript.osce.schedule.exam_date).toLocaleDateString()}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">Center</p>
                    <p className="text-sm font-medium text-[#1B2A4A]">{transcript.osce.schedule.center_name || '-'}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">Status</p>
                    <Badge className={`text-xs rounded-sm ${transcript.osce.schedule.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {transcript.osce.schedule.status || 'Scheduled'}
                    </Badge>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500">Grade</p>
                    <p className="text-lg font-bold text-[#1B2A4A]">{transcript.osce.grade || '-'}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No OSCE examination scheduled yet.</p>
              )}
            </CardContent>
          </Card>

          {/* Case Log Summary */}
          <Card className="imcac-card print:shadow-none print:border print:border-slate-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4 flex items-center">
                <ClipboardList className="h-5 w-5 mr-2 text-[#C5A572]" /> Case Log Portfolio Summary
              </h3>
              {transcript?.case_logs?.entries?.length > 0 ? (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-2xl font-bold text-[#1B2A4A]">{transcript.case_logs.total_submitted}</p>
                      <p className="text-xs text-slate-500">Total Cases</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-2xl font-bold text-[#1B2A4A]">{transcript.case_logs.entries.filter(e => e.scoring).length}</p>
                      <p className="text-xs text-slate-500">Scored</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-2xl font-bold text-[#1B2A4A]">{caseLogAvg?.toFixed(1) || '-'}</p>
                      <p className="text-xs text-slate-500">Average Score</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 px-3 font-semibold text-xs">Date</th>
                          <th className="text-left py-2 px-3 font-semibold text-xs">Procedure</th>
                          <th className="text-center py-2 px-3 font-semibold text-xs">Clinical</th>
                          <th className="text-center py-2 px-3 font-semibold text-xs">Technique</th>
                          <th className="text-center py-2 px-3 font-semibold text-xs">Documentation</th>
                          <th className="text-center py-2 px-3 font-semibold text-xs">Safety</th>
                          <th className="text-center py-2 px-3 font-semibold text-xs">Overall</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transcript.case_logs.entries.filter(e => e.scoring).slice(0, 10).map((entry, idx) => (
                          <tr key={idx} className="border-b border-slate-100">
                            <td className="py-2 px-3">{new Date(entry.created_at).toLocaleDateString()}</td>
                            <td className="py-2 px-3">{entry.procedure_type}</td>
                            <td className="py-2 px-3 text-center">{entry.scoring.clinical_reasoning_score}/10</td>
                            <td className="py-2 px-3 text-center">{entry.scoring.technique_score}/10</td>
                            <td className="py-2 px-3 text-center">{entry.scoring.documentation_score}/10</td>
                            <td className="py-2 px-3 text-center">{entry.scoring.patient_safety_score}/10</td>
                            <td className="py-2 px-3 text-center font-semibold">{entry.scoring.overall_score}/10</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No case logs with scores yet.</p>
              )}
            </CardContent>
          </Card>

          {/* Viva Voce Results */}
          {transcript?.comprehensive_scoring && (
            <Card className="imcac-card mb-6 print:shadow-none print:border print:border-slate-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4 flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2 text-[#C5A572]" /> Viva Voce Assessment
                </h3>
                {transcript.comprehensive_scoring.viva_overall ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-500">Knowledge</p>
                      <p className="text-lg font-bold text-[#1B2A4A]">{transcript.comprehensive_scoring.viva_knowledge}/10</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-500">Communication</p>
                      <p className="text-lg font-bold text-[#1B2A4A]">{transcript.comprehensive_scoring.viva_communication}/10</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-500">Clinical Reasoning</p>
                      <p className="text-lg font-bold text-[#1B2A4A]">{transcript.comprehensive_scoring.viva_clinical_reasoning}/10</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-sm">
                      <p className="text-xs text-blue-600">Overall Score</p>
                      <p className="text-lg font-bold text-blue-700">{transcript.comprehensive_scoring.viva_overall}/10</p>
                      <Badge className={`mt-1 text-xs ${transcript.comprehensive_scoring.viva_overall >= 5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {transcript.comprehensive_scoring.viva_overall >= 5 ? 'Pass' : 'Fail'}
                      </Badge>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">Viva Voce assessment not yet completed.</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Ethics & Governance Results */}
          {transcript?.comprehensive_scoring && (
            <Card className="imcac-card mb-6 print:shadow-none print:border print:border-slate-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4 flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-[#C5A572]" /> Ethics & Governance Assessment
                </h3>
                {transcript.comprehensive_scoring.ethics_overall ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-500">Ethics Knowledge</p>
                      <p className="text-lg font-bold text-[#1B2A4A]">{transcript.comprehensive_scoring.ethics_knowledge}/10</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-500">Governance</p>
                      <p className="text-lg font-bold text-[#1B2A4A]">{transcript.comprehensive_scoring.ethics_governance}/10</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-500">Professionalism</p>
                      <p className="text-lg font-bold text-[#1B2A4A]">{transcript.comprehensive_scoring.ethics_professionalism}/10</p>
                    </div>
                    <div className="text-center p-3 bg-indigo-50 rounded-sm">
                      <p className="text-xs text-indigo-600">Overall Score</p>
                      <p className="text-lg font-bold text-indigo-700">{transcript.comprehensive_scoring.ethics_overall}/10</p>
                      <Badge className={`mt-1 text-xs ${transcript.comprehensive_scoring.ethics_overall >= 5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {transcript.comprehensive_scoring.ethics_overall >= 5 ? 'Pass' : 'Fail'}
                      </Badge>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">Ethics assessment not yet completed.</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Final Result & Marksheet */}
          {transcript?.comprehensive_scoring?.final_result && (
            <Card className={`imcac-card mb-6 print:shadow-none print:border ${
              transcript.comprehensive_scoring.final_result === 'Pass' ? 'border-green-300 bg-green-50' : 
              transcript.comprehensive_scoring.final_result === 'Fail' ? 'border-red-300 bg-red-50' : 
              'border-amber-300 bg-amber-50'
            }`}>
              <CardContent className="p-6 text-center">
                <h3 className="text-2xl font-bold text-[#1B2A4A] font-heading mb-2">
                  Final Result: <span className={
                    transcript.comprehensive_scoring.final_result === 'Pass' ? 'text-green-600' : 
                    transcript.comprehensive_scoring.final_result === 'Fail' ? 'text-red-600' : 'text-amber-600'
                  }>{transcript.comprehensive_scoring.final_result}</span>
                </h3>
                {transcript.marksheet && (
                  <div className="mt-4">
                    <p className="text-sm text-slate-600 mb-2">
                      Marksheet Number: <span className="font-mono font-semibold">{transcript.marksheet.marksheet_number}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      Issued: {new Date(transcript.marksheet.issued_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {!transcript.marksheet && transcript.comprehensive_scoring.submitted_to_admin && (
                  <p className="text-sm text-amber-600 mt-2">
                    Your scores have been submitted. Marksheet will be available after admin review.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Print Footer */}
          <div className="hidden print:block mt-8 pt-4 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-500">This transcript is generated by IMCAC (International Medical Competency Accreditation Council)</p>
            <p className="text-xs text-slate-400">Verify certificates at https://www.imcac.org/verification</p>
          </div>
        </div>
      </div>
    </div>
  );
}
